/**
 * Voice AI System — "Warm Emerald & Aged Gold" Design System
 * Premium light theme with warm stone palette + emerald primary
 * FontAwesome Pro 6.7.2 Sharp Light + Sharp Duotone Solid
 * Typography: Satoshi (display) + DM Sans (body) + JetBrains Mono (data)
 */

import { css } from 'lit';

export const designSystem = css`
  :host {
    /* ─── Primary Colors (Warm Emerald) ─── */
    --color-primary: #2D8F6F;
    --color-primary-hover: #257A5E;
    --color-primary-deep: #1D6B50;
    --color-primary-light: #F0F9F5;
    --color-primary-subtle: #D4EEDF;
    --color-primary-glow: rgba(45, 143, 111, 0.14);

    /* ─── Accent Colors (Warm Mineral) ─── */
    --color-accent: #8B7D5E;
    --color-accent-hover: #7A6D50;
    --color-accent-light: #F9F7F3;
    --color-accent-subtle: #EDE8DF;
    --color-accent-deep: #6B5F45;
    --color-accent-brown: #5C5240;
    --color-accent-glow: rgba(139, 125, 94, 0.18);

    /* ─── KPI Accent Palette (4 distinct mineral tones) ─── */
    --kpi-teal: #2D8F6F;
    --kpi-amber: #C4956A;
    --kpi-slate: #6B7FA3;
    --kpi-sage: #7EA37B;

    /* ─── Brand Gradients ─── */
    --gradient-brand: linear-gradient(135deg, #2D8F6F 0%, #3CAE89 100%);
    --gradient-brand-hover: linear-gradient(135deg, #257A5E 0%, #2D9B78 100%);
    --gradient-brand-hero: linear-gradient(135deg, #2D8F6F 0%, #B8943F 100%);
    --gradient-gold: linear-gradient(135deg, #8B7D5E 0%, #A89778 100%);
    --gradient-gold-subtle: linear-gradient(135deg, rgba(139, 125, 94, 0.1) 0%, rgba(139, 125, 94, 0.04) 100%);

    /* ─── Semantic Colors ─── */
    --color-success: #16A34A;
    --color-success-light: #F0FDF4;
    --color-success-bg: #DCFCE7;
    --color-success-text: #15803D;

    --color-warning: #CA8A04;
    --color-warning-light: #FEFCE8;
    --color-warning-bg: #FEF9C3;
    --color-warning-text: #A16207;

    --color-error: #DC2626;
    --color-error-light: #FEF2F2;
    --color-error-bg: #FEE2E2;
    --color-error-text: #B91C1C;

    --color-info: #2563EB;
    --color-info-light: #EEF2FF;
    --color-info-bg: #E0E7FF;
    --color-info-text: #1E40AF;

    /* ─── Neutral Palette (Warm Stone) ─── */
    --color-bg-primary: #FFFFFF;
    --color-bg-secondary: #FFFFFF;
    --color-bg-tertiary: #F5F3F0;
    --color-bg-elevated: #FFFFFF;
    --color-bg-inset: #EEECE8;
    --color-bg-hover: #F3F1EE;

    /* ─── Gradient Surfaces ─── */
    --gradient-sidebar: linear-gradient(180deg, #FAFAF8 0%, #F7F6F3 100%);
    --gradient-warm: linear-gradient(135deg, #F7FAF8 0%, #F0F9F5 100%);
    --gradient-card-glow: linear-gradient(135deg, rgba(139, 125, 94, 0.04) 0%, rgba(45, 143, 111, 0.02) 100%);

    /* ─── Frame Colors ─── */
    --color-frame-bg: #FFFFFF;
    --color-frame-border: rgba(255, 255, 255, 0.06);

    --color-text-primary: #1C1917;
    --color-text-secondary: #57534E;
    --color-text-tertiary: #A8A29E;
    --color-text-disabled: #D6D3D1;
    --color-text-inverse: #FFFFFF;

    --color-border-primary: #E7E5E4;
    --color-border-secondary: #D6D3D1;
    --color-border-subtle: #F5F5F4;
    --color-border-focus: #2D8F6F;

    /* ─── Glass Surfaces (frosted glass over bokeh bg) ─── */
    --glass-bg: rgba(255, 255, 255, 0.18);
    --glass-bg-hover: rgba(255, 255, 255, 0.25);
    --glass-bg-light: rgba(255, 255, 255, 0.12);
    --glass-bg-light-hover: rgba(255, 255, 255, 0.2);
    --glass-bg-input: rgba(255, 255, 255, 0.5);
    --glass-bg-modal: rgba(255, 253, 250, 0.97);
    --glass-border: rgba(255, 255, 255, 0.35);
    --glass-border-light: rgba(255, 255, 255, 0.25);
    --glass-border-hover: rgba(255, 255, 255, 0.45);
    --glass-divider: rgba(255, 255, 255, 0.2);
    --glass-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);

    /* ─── Shadows & Elevation (Luxury depth) ─── */
    --shadow-xs: 0 1px 2px rgba(28, 25, 23, 0.04);
    --shadow-sm: 0 1px 3px rgba(28, 25, 23, 0.06), 0 1px 2px rgba(28, 25, 23, 0.04);
    --shadow-md: 0 4px 12px -2px rgba(28, 25, 23, 0.08), 0 1px 3px rgba(28, 25, 23, 0.04);
    --shadow-lg: 0 12px 32px -8px rgba(28, 25, 23, 0.12), 0 4px 8px -2px rgba(28, 25, 23, 0.04);
    --shadow-xl: 0 24px 48px -12px rgba(28, 25, 23, 0.16), 0 8px 16px -4px rgba(28, 25, 23, 0.06);
    --shadow-elevated: 0 8px 24px -4px rgba(28, 25, 23, 0.08), 0 2px 6px rgba(28, 25, 23, 0.03);
    --shadow-hover-lift: 0 12px 32px -6px rgba(28, 25, 23, 0.12), 0 4px 12px -2px rgba(28, 25, 23, 0.04);
    --shadow-glow-primary: 0 0 0 3px rgba(45, 143, 111, 0.15), 0 4px 12px rgba(45, 143, 111, 0.08);
    --shadow-glow-accent: 0 0 0 3px rgba(139, 125, 94, 0.2), 0 4px 16px rgba(139, 125, 94, 0.12);
    --shadow-glow-gold: 0 0 0 3px rgba(139, 125, 94, 0.25), 0 4px 20px rgba(139, 125, 94, 0.15);
    --shadow-primary-btn: 0 2px 4px rgba(45, 143, 111, 0.2), 0 1px 2px rgba(45, 143, 111, 0.12);
    --shadow-inset-subtle: inset 0 1px 2px rgba(28, 25, 23, 0.04);

    /* ─── Typography ─── */
    --font-display: 'Satoshi', sans-serif;
    --font-body: 'DM Sans', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;

    --text-xs:   0.6875rem;
    --text-sm:   0.8125rem;
    --text-base: 0.9375rem;
    --text-lg:   1.0625rem;
    --text-xl:   1.25rem;
    --text-2xl:  1.5rem;
    --text-3xl:  1.875rem;
    --text-4xl:  2.5rem;
    --text-5xl:  3.25rem;

    --leading-tight: 1.2;
    --leading-normal: 1.5;
    --leading-relaxed: 1.625;

    --tracking-tighter: -0.04em;
    --tracking-tight: -0.025em;
    --tracking-normal: 0;
    --tracking-wide: 0.025em;
    --tracking-wider: 0.05em;

    --font-normal: 400;
    --font-medium: 500;
    --font-semibold: 600;
    --font-bold: 700;
    --font-black: 900;

    /* ─── Spacing ─── */
    --space-0: 0;
    --space-px: 1px;
    --space-0\.5: 0.125rem;
    --space-1: 0.25rem;
    --space-1\.5: 0.375rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-14: 3.5rem;
    --space-16: 4rem;
    --space-20: 5rem;
    --space-24: 6rem;

    /* ─── Border Radius ─── */
    --radius-sm: 8px;
    --radius-md: 10px;
    --radius-lg: 14px;
    --radius-xl: 20px;
    --radius-2xl: 24px;
    --radius-3xl: 32px;
    --radius-full: 9999px;

    /* ─── Transitions ─── */
    --ease-out: cubic-bezier(0.16, 1, 0.3, 1);
    --ease-in-out: cubic-bezier(0.65, 0, 0.35, 1);
    --duration-fast: 120ms;
    --duration-base: 200ms;
    --duration-slow: 350ms;
    --duration-slower: 500ms;

    --ease-luxury: cubic-bezier(0.22, 1, 0.36, 1);
    --duration-page: 450ms;

    --transition-fast: var(--duration-fast) var(--ease-out);
    --transition-base: var(--duration-base) var(--ease-out);
    --transition-slow: var(--duration-slow) var(--ease-out);
    --transition-page: var(--duration-page) var(--ease-luxury);

    /* ─── Z-Index ─── */
    --z-dropdown: 1000;
    --z-sticky: 1020;
    --z-fixed: 1030;
    --z-modal-backdrop: 1040;
    --z-modal: 1050;
    --z-popover: 1060;
    --z-tooltip: 1070;
    --z-toast: 1080;

    /* ─── Layout ─── */
    --sidebar-width: 264px;
    --sidebar-collapsed-width: 72px;
    --topbar-height: 0px;
    --content-max-width: 1440px;
  }

  /* Global Reset */
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-border-secondary);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-tertiary);
  }

  /* Selection */
  ::selection {
    background: var(--color-primary-subtle);
    color: var(--color-primary);
  }

  /* ─── Loading Skeleton (shared across all pages) ─── */
  .skeleton {
    background-image: linear-gradient(90deg, var(--color-bg-tertiary) 0%, var(--color-accent-light) 50%, var(--color-bg-tertiary) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s var(--ease-in-out) infinite;
    border-radius: var(--radius-md);
  }

  /* Shimmer line — default 14px height, use size modifiers below */
  .skeleton-line {
    height: 14px;
    background-image: linear-gradient(90deg, var(--color-border-primary) 25%, var(--color-bg-tertiary) 50%, var(--color-border-primary) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s var(--ease-in-out) infinite;
    border-radius: var(--radius-sm);
  }

  /* Width modifiers */
  .skeleton-line.short  { width: 40%; }
  .skeleton-line.medium { width: 65%; }
  .skeleton-line.long   { width: 90%; }
  .skeleton-line.full   { width: 100%; }

  /* Height modifiers */
  .skeleton-line.xs { height: 10px; }
  .skeleton-line.sm { height: 12px; }
  .skeleton-line.lg { height: 18px; }

  /* Circle — avatar placeholder */
  .skeleton-line.circle {
    width: 44px;
    height: 44px;
    border-radius: var(--radius-full);
  }

  /* Card-shaped skeleton */
  .skeleton-card {
    border-radius: var(--radius-xl);
    background-image: linear-gradient(90deg, var(--color-bg-tertiary) 0%, var(--color-accent-light) 50%, var(--color-bg-tertiary) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s var(--ease-in-out) infinite;
    border: 1px solid rgba(255, 255, 255, 0.35);
  }

  /* Toggle switch skeleton */
  .skeleton-toggle {
    width: 40px;
    height: 22px;
    border-radius: var(--radius-full);
    background-image: linear-gradient(90deg, var(--color-bg-tertiary) 0%, var(--color-accent-light) 50%, var(--color-bg-tertiary) 100%);
    background-size: 200% 100%;
    animation: shimmer 1.5s var(--ease-in-out) infinite;
    flex-shrink: 0;
  }

  /* Row skeleton — flex container for table/list rows */
  .skeleton-row {
    display: flex;
    gap: var(--space-4);
    align-items: center;
    padding: var(--space-3) 0;
  }
  .skeleton-row + .skeleton-row {
    border-top: 1px solid var(--color-border-subtle);
  }
  .skeleton-row td {
    padding: var(--space-3) var(--space-4) !important;
  }

  /* Stat card skeleton — makes inner text/icon transparent, shows shimmer */
  .skeleton-stat .metric-value,
  .skeleton-stat .metric-label,
  .skeleton-stat .mini-stat-value,
  .skeleton-stat .mini-stat-label {
    background-image: linear-gradient(90deg, var(--color-border-primary) 25%, var(--color-bg-tertiary) 50%, var(--color-border-primary) 75%) !important;
    background-size: 200% 100% !important;
    animation: shimmer 1.5s var(--ease-in-out) infinite !important;
    border-radius: var(--radius-sm);
    color: transparent !important;
  }
  .skeleton-stat .stat-icon,
  .skeleton-stat .mini-stat-icon {
    background-image: linear-gradient(90deg, var(--color-border-primary) 25%, var(--color-bg-tertiary) 50%, var(--color-border-primary) 75%) !important;
    background-size: 200% 100% !important;
    animation: shimmer 1.5s var(--ease-in-out) infinite !important;
    color: transparent !important;
  }

  /* ─── Animations ─── */
  @keyframes softPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

export const componentStyles = css`
  /* ─── Card ─── */
  .card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
    box-shadow: var(--shadow-sm);
    transition: all var(--transition-base);
  }

  .card:hover {
    box-shadow: var(--shadow-elevated);
    border-color: var(--color-border-secondary);
    transform: scale(1.005);
  }

  .card-flat {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-xl);
    padding: var(--space-6);
  }

  /* ─── Buttons ─── */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: 10px var(--space-5);
    min-height: 40px;
    border: 1px solid transparent;
    border-radius: var(--radius-lg);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-semibold);
    cursor: pointer;
    transition: all var(--transition-base);
    white-space: nowrap;
    line-height: var(--leading-normal);
    text-decoration: none;
    letter-spacing: 0.01em;
    box-sizing: border-box;
  }

  .btn:focus-visible {
    outline: none;
    box-shadow: var(--shadow-glow-gold);
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background: #2D8F6F;
    color: var(--color-text-inverse);
    box-shadow: var(--shadow-primary-btn);
  }

  .btn-primary:hover:not(:disabled) {
    background: #257A5E;
    box-shadow: 0 4px 16px rgba(45, 143, 111, 0.3), 0 2px 4px rgba(45, 143, 111, 0.15);
  }

  .btn-secondary {
    background: var(--color-bg-secondary);
    color: var(--color-text-secondary);
    border-color: var(--color-border-primary);
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
    border-color: var(--color-border-secondary);
  }

  .btn-danger {
    background: var(--color-error);
    color: white;
  }

  .btn-danger:hover:not(:disabled) {
    opacity: 0.9;
  }

  .btn-danger-ghost {
    background: transparent;
    color: var(--color-error);
    border-color: transparent;
  }

  .btn-danger-ghost:hover:not(:disabled) {
    background: var(--color-error-bg);
  }

  .btn-default {
    background: transparent;
    color: var(--color-primary);
    border-color: var(--color-primary-subtle);
  }

  .btn-default:hover:not(:disabled) {
    background: var(--color-primary-light);
  }

  .btn-ghost {
    background: transparent;
    color: var(--color-text-secondary);
  }

  .btn-ghost:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
    color: var(--color-text-primary);
  }

  .btn-sm {
    padding: var(--space-1) var(--space-3);
    font-size: var(--text-xs);
    min-height: 30px;
  }

  .btn-lg {
    padding: var(--space-3) var(--space-8);
    font-size: var(--text-base);
    border-radius: var(--radius-lg);
  }

  .btn-icon {
    width: 40px;
    height: 40px;
    min-height: auto;
    padding: 0;
    border-radius: var(--radius-md);
  }

  .btn-icon.sm {
    width: 28px;
    height: 28px;
  }

  /* ─── Form Elements (Global Defaults) ─── */
  .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
  }

  .form-grid.full {
    grid-template-columns: 1fr;
  }

  .form-grid.cols-3 {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    .form-grid { grid-template-columns: 1fr; }
    .form-grid.cols-3 { grid-template-columns: 1fr; }
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-1\.5);
  }

  .form-group.full {
    grid-column: 1 / -1;
  }

  .form-label {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-secondary);
  }

  .form-input,
  .form-select,
  .form-textarea {
    padding: 10px var(--space-3);
    min-height: 40px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    transition: all var(--transition-fast);
    width: 100%;
    box-sizing: border-box;
  }

  .form-input:focus,
  .form-select:focus,
  .form-textarea:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .form-textarea {
    min-height: 100px;
    resize: vertical;
    line-height: var(--leading-relaxed);
  }

  .form-hint {
    font-family: var(--font-body);
    font-size: var(--text-xs);
    color: var(--color-text-tertiary);
  }

  /* ─── Search Input ─── */
  .search-input-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 360px;
  }

  .search-input-wrap i {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: var(--space-2) var(--space-3) var(--space-2) var(--space-8);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    color: var(--color-text-primary);
    font-size: var(--text-sm);
    transition: all var(--transition-fast);
    box-sizing: border-box;
  }

  .search-input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  /* ─── Badges ─── */
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: var(--radius-full);
    font-family: var(--font-body);
    font-size: var(--text-xs);
    font-weight: var(--font-bold);
    line-height: var(--leading-normal);
    letter-spacing: 0.02em;
  }

  .badge-success {
    background: var(--color-success-bg);
    color: var(--color-success-text);
  }

  .badge-warning {
    background: var(--color-warning-bg);
    color: var(--color-warning-text);
  }

  .badge-error {
    background: var(--color-error-bg);
    color: var(--color-error-text);
  }

  .badge-info {
    background: var(--color-info-bg);
    color: var(--color-info-text);
  }

  .badge-primary {
    background: var(--color-primary-subtle);
    color: var(--color-primary);
  }

  .badge-neutral {
    background: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
  }

  /* ─── Status Dot ─── */
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: var(--radius-full);
    flex-shrink: 0;
  }

  .status-dot.active {
    background: var(--color-success);
    box-shadow: 0 0 0 3px var(--color-success-bg);
    animation: statusBreathe 2.5s ease-in-out infinite;
  }

  .status-dot.warning {
    background: var(--color-warning);
    box-shadow: 0 0 0 3px var(--color-warning-bg);
  }

  .status-dot.error {
    background: var(--color-error);
    box-shadow: 0 0 0 3px var(--color-error-bg);
  }

  .status-dot.inactive {
    background: var(--color-text-disabled);
  }

  .status-dot.info {
    background: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-accent-subtle);
  }

  /* ─── Metric Display ─── */
  .metric {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .metric-label {
    font-family: var(--font-body);
    font-size: 0.625rem;
    color: var(--color-text-tertiary);
    font-weight: var(--font-bold);
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }

  .metric-value {
    font-family: var(--font-display);
    font-size: var(--text-3xl);
    color: var(--color-text-primary);
    font-weight: var(--font-black);
    line-height: var(--leading-tight);
    letter-spacing: var(--tracking-tight);
  }

  .metric-value.lg {
    font-size: var(--text-4xl);
  }

  .metric-change {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    font-weight: var(--font-medium);
  }

  .metric-change.positive {
    color: var(--color-success);
  }

  .metric-change.negative {
    color: var(--color-error);
  }

  /* ─── Data Table ─── */
  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-family: var(--font-body);
  }

  .data-table thead th {
    padding: var(--space-3) var(--space-5);
    text-align: left;
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-text-tertiary);
    text-transform: uppercase;
    letter-spacing: var(--tracking-wider);
    border-bottom: 2px solid var(--color-border-primary);
    background: var(--color-bg-tertiary);
    white-space: nowrap;
    cursor: default;
    user-select: none;
  }

  .data-table thead th.sortable {
    cursor: pointer;
  }

  .data-table thead th.sortable:hover {
    color: var(--color-text-primary);
  }

  .data-table tbody td {
    padding: var(--space-4) var(--space-5);
    font-size: var(--text-sm);
    color: var(--color-text-secondary);
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: middle;
  }

  .data-table tbody tr {
    transition: all var(--transition-base);
  }

  .data-table tbody tr:hover {
    background: var(--color-bg-hover);
    box-shadow: inset 3px 0 0 var(--color-accent);
  }

  .data-table tbody tr:last-child td {
    border-bottom: none;
  }

  /* ─── Input Fields ─── */
  .input {
    width: 100%;
    padding: var(--space-2\.5, 0.625rem) var(--space-4);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-primary);
    border-radius: var(--radius-md);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    transition: all var(--transition-base);
    line-height: var(--leading-normal);
  }

  .input:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: var(--shadow-glow-primary);
  }

  .input::placeholder {
    color: var(--color-text-tertiary);
  }

  .input-with-icon {
    position: relative;
  }

  .input-with-icon .input {
    padding-left: var(--space-10);
  }

  .input-with-icon .input-icon {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-tertiary);
    font-size: var(--text-sm);
    pointer-events: none;
  }

  /* ─── Tab Bar ─── */
  .tab-bar {
    display: flex;
    gap: var(--space-1);
    border-bottom: 1px solid var(--color-border-primary);
    margin-bottom: var(--space-6);
  }

  .tab-item {
    padding: var(--space-3) var(--space-5);
    font-family: var(--font-body);
    font-size: var(--text-sm);
    font-weight: var(--font-medium);
    color: var(--color-text-tertiary);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: all var(--transition-base);
    white-space: nowrap;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
  }

  .tab-item:hover {
    color: var(--color-text-secondary);
    background: var(--color-bg-hover);
  }

  .tab-item.active {
    color: var(--color-accent);
    border-bottom-color: var(--color-accent);
    background: var(--color-accent-light);
    font-weight: var(--font-semibold);
  }

  /* ─── Section Headers ─── */
  .section-title {
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: var(--font-bold);
    color: var(--color-text-primary);
    letter-spacing: var(--tracking-tight);
  }

  .section-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    margin-top: var(--space-1);
  }

  /* Page-level display title */
  .page-title-xl {
    font-family: var(--font-display);
    font-size: var(--text-4xl);
    font-weight: var(--font-black);
    color: var(--color-text-primary);
    letter-spacing: -0.04em;
    line-height: var(--leading-tight);
  }

  /* ─── Toggle Switch ─── */
  .toggle {
    position: relative;
    width: 40px;
    height: 22px;
    background: var(--color-border-secondary);
    border-radius: var(--radius-full);
    cursor: pointer;
    transition: background var(--transition-fast);
    flex-shrink: 0;
  }

  .toggle.active {
    background: var(--color-primary);
  }

  .toggle::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: var(--radius-full);
    box-shadow: var(--shadow-sm);
    transition: transform var(--transition-fast);
  }

  .toggle.active::after {
    transform: translateX(18px);
  }

  /* ─── Empty State ─── */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--space-16) var(--space-8);
    text-align: center;
  }

  .empty-state-icon {
    font-size: 3rem;
    color: var(--color-text-disabled);
    margin-bottom: var(--space-4);
  }

  .empty-state-title {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    color: var(--color-text-secondary);
    margin-bottom: var(--space-2);
  }

  .empty-state-description {
    font-family: var(--font-body);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    max-width: 360px;
  }

  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.96); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  @keyframes countUp {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes statusBreathe {
    0%, 100% { opacity: 1; box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2); }
    50% { opacity: 0.7; box-shadow: 0 0 0 6px rgba(22, 163, 74, 0.08); }
  }

  @keyframes glowPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(45, 143, 111, 0); }
    50% { box-shadow: 0 0 0 6px rgba(45, 143, 111, 0.1); }
  }

  @keyframes staggerFadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes pageReveal {
    from { opacity: 0; transform: translateX(12px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes cardReveal {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }

  /* ─── Utility Classes ─── */
  .animate-in {
    animation: pageReveal var(--duration-page) var(--ease-luxury) both;
  }

  .mono {
    font-family: var(--font-mono);
  }

  .truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  /* ─── Stagger Delays ─── */
  .stagger-1 { animation-delay: 60ms; }
  .stagger-2 { animation-delay: 120ms; }
  .stagger-3 { animation-delay: 180ms; }
  .stagger-4 { animation-delay: 240ms; }
  .stagger-5 { animation-delay: 300ms; }
  .stagger-6 { animation-delay: 360ms; }

  /* ─── Icon Container ─── */
  .icon-bg {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-lg);
    flex-shrink: 0;
  }

  .icon-bg-gradient {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-lg);
    flex-shrink: 0;
    background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-primary-subtle) 100%);
    color: var(--color-primary);
  }
`;
